// Candidates, their notes and their tags — TODO T30 (1 Sep 2026); the calls are T17's and T18's.
//
// THERE IS NO `createCandidate` HERE AND THERE WILL NOT BE. `candidates` is agent-owned and the
// BFF holds no INSERT on it: entering a record is the forward-deployed headhunter's job, from the
// terminal (`pnpm --filter @vera/db run candidate:enter`, T18a). T18a chose a dedicated operator
// role over a brain carve-out precisely so this app could not grow a candidate form by
// accident (packages/db/src/ownership.ts).
//
// What the brain owns is everything ABOUT a candidate — the notes and the tags below, the stage
// in each mandate (api/searches.ts) and the invitation codes (api/codes.ts).
//
// Tags live in this module rather than one of their own because a tag exists to be put on a
// candidate: every write below invalidates the same keys, and a `tags` module would be two
// endpoints and an import cycle.
import { keepPreviousData, useQuery, type UseQueryResult } from '@tanstack/react-query';
import type {
  CandidateDetail,
  CandidateListItem,
  CandidateSort,
  PipelineStage,
  ProactiveCompany,
  ProactiveList,
  ProactiveSort,
  RolePickPutForwardResponse,
  SearchCompany,
  SearchConversationList,
  SearchConversationSort,
  TagRow,
} from '@vera/bff';
import type { ProactiveStage } from '@vera/db/proactive';
import type { SearchGroup } from '@vera/db/search-groups';
import type { ProactiveGroup, ProactiveWaiting } from '@vera/db/proactive-groups';
import { API_PREFIX, query, type RequestFn } from './core.ts';
import { POLL_MS, STALE_MS } from './intervals.ts';
import { KEYS, useConsoleMutation } from './mutation.ts';
import { useRequest } from './provider.tsx';

/**
 * The list's filters and its sort — T36 (2 Sep 2026). Every one of them goes to the SERVICE,
 * because the list is capped: a filter over the page the browser holds would miss whoever was
 * past the cap, which is exactly who a filter is for. The screen keeps them in the URL.
 */
export interface ListCandidatesQuery {
  searchId?: string;
  /** At this stage — on `searchId` when both are given, on any search otherwise. */
  stage?: PipelineStage;
  /** Who is NOT on this search yet — what the "add to pipeline" picker asks for. */
  notInSearchId?: string;
  tagId?: string;
  location?: string;
  q?: string;
  /** T121: `key:value`, one a question the list filters by (`ANSWER_FILTERS`), checked by the service. */
  answer?: readonly string[];
  sort?: CandidateSort;
  limit?: number;
  /** T273: No-man's-land's people (everyone not on a live Search); every other value is All. */
  scope?: 'all' | 'nomansland';
  /** T273: All's Rejected filter: a Search entry at Rejected, or a role with no Search at Rejected. */
  rejected?: boolean;
}

/** T273: Candidates → Proactive's filters: the list's person ones, a company and a stage. */
export interface ProactiveQuery {
  stage?: ProactiveStage;
  clientId?: string;
  /** T296: one role, from the Board's row. */
  roleId?: string;
  tagId?: string;
  location?: string;
  q?: string;
  answer?: readonly string[];
  /** T292: the Conversations view adds `waiting` (longest at their stage) and `message`. */
  sort?: ProactiveSort;
  /** T292: the Conversations view's tab. */
  group?: ProactiveGroup;
  /** T292: whose move it is. */
  waitingOn?: ProactiveWaiting;
  /** T296c: the Board's read: the roles and tiles, no rows. */
  view?: 'board';
}

/** T292a: Proactive's companies — one company, a company's or contact's name, whose move. */
export interface ProactiveCompaniesQuery {
  clientId?: string;
  q?: string;
  waitingOn?: ProactiveWaiting;
  /** T296: only the contacts with a thread, dated and ordered by them (`keepThreads`). */
  threads?: boolean;
}

/** T296: Searches → Conversations, the Searches mirror of `ProactiveQuery`. */
export interface SearchConversationsQuery {
  group?: SearchGroup;
  stage?: PipelineStage;
  searchId?: string;
  clientId?: string;
  q?: string;
  sort?: SearchConversationSort;
}

export const candidatesApi = (request: RequestFn) => ({
  /** T296: Searches → Conversations, a row a person on a Search with a thread with kadmon. */
  searchConversations: (options: SearchConversationsQuery = {}) =>
    request<SearchConversationList>(`${API_PREFIX}/search-conversations${query({ ...options })}`),
  /** T296: Searches' companies, each contact's thread and who was put to them. */
  searchCompanies: async (options: { q?: string } = {}) =>
    (
      await request<{ companies: SearchCompany[] }>(
        `${API_PREFIX}/search-conversations/companies${query({ ...options })}`,
      )
    ).companies,
  list: async (options: ListCandidatesQuery = {}) =>
    (
      await request<{ candidates: CandidateListItem[] }>(
        `${API_PREFIX}/candidates${query({ ...options, rejected: options.rejected ? 1 : undefined })}`,
      )
    ).candidates,
  /** T456a: kadmon's fit, rows and all, for one person on one Proactive role or Search. */
  roleFit: (candidateId: string, ref: RoleFitRef) =>
    request<RoleFitRead>(
      `${API_PREFIX}/candidates/${encodeURIComponent(candidateId)}/role-fit${query({ ...ref })}`,
    ),
  /** T273: Candidates → Proactive, a row a person and role at the stage the records put it. */
  proactive: (options: ProactiveQuery = {}) =>
    request<ProactiveList>(`${API_PREFIX}/proactive${query({ ...options })}`),
  /** T292a: Proactive's companies, each contact's thread and who was sent to them. */
  proactiveCompanies: async (options: ProactiveCompaniesQuery = {}) =>
    (
      await request<{ companies: ProactiveCompany[] }>(
        `${API_PREFIX}/proactive/companies${query({ ...options })}`,
      )
    ).companies,
  get: (candidateId: string) => request<CandidateDetail>(`${API_PREFIX}/candidates/${candidateId}`),
  /**
   * T167h: the team's yes or no to a role kadmon put to it about this person. Filed for the
   * worker to record (202); the page polls until it lands.
   */
  decideRoleProposal: (roleProposalId: string, decision: 'yes' | 'no') =>
    request<{ id: string }>(`${API_PREFIX}/role-proposals/${roleProposalId}/decide`, {
      method: 'POST',
      body: { decision },
    }),
  /**
   * T431: Match Now — kadmon looks for roles for this person at once. Filed for the worker (202);
   * any matches are offered to them, the team asked when they pick one (T515).
   */
  matchNow: (conversationId: string) =>
    request<{ id: string }>(`${API_PREFIX}/conversations/${conversationId}/match-now`, {
      method: 'POST',
      body: {},
    }),
  /** T167i: their yes to a Potential or Client company's role, put forward with one tap. */
  putForwardPick: (roleOfferId: string) =>
    request<RolePickPutForwardResponse>(`${API_PREFIX}/role-offers/${roleOfferId}/put-forward`, {
      method: 'POST',
      body: {},
    }),
  /**
   * T205: meet them first instead: kadmon tells them the team would love to meet them about that
   * role; nothing goes to the company. Filed for the worker, so 202, and the page polls.
   */
  meetPick: (roleOfferId: string) =>
    request<{ id: string; created: boolean }>(`${API_PREFIX}/role-offers/${roleOfferId}/meet`, {
      method: 'POST',
      body: {},
    }),
  /**
   * T272: the team's yes or no to their pick of a role offered straight. Filed for the worker to
   * record (202); the page polls until it lands.
   */
  decidePick: (roleOfferId: string, answer: 'yes' | 'no') =>
    request<{ id: string }>(`${API_PREFIX}/role-offers/${roleOfferId}/team-answer`, {
      method: 'POST',
      body: { answer },
    }),
  /**
   * T151: the person's profile as a PDF, as bytes; the page saves them (profilePdf.ts). Since T277
   * (5 Oct 2026) read against a Search (`searchId`; null for none, absent for the service's choice:
   * their only Search), and with `ids` several people in one PDF, this person first.
   */
  profilePdf: (
    candidateId: string,
    options: { searchId?: string | null; roleId?: string; ids?: readonly string[] } = {},
  ) => {
    const query = new URLSearchParams();
    // T429h: a Proactive role (`roleId`) is read against in place of a Search, never beside one.
    if (options.roleId) query.set('role', options.roleId);
    else if (options.searchId !== undefined) query.set('search', options.searchId ?? 'none');
    if (options.ids && options.ids.length > 1) query.set('ids', options.ids.join(','));
    const qs = query.toString();
    return request<ArrayBuffer>(
      `${API_PREFIX}/candidates/${candidateId}/profile.pdf${qs ? `?${qs}` : ''}`,
      { binary: true },
    );
  },
  listTags: async () => (await request<{ tags: TagRow[] }>(`${API_PREFIX}/tags`)).tags,

  addNote: async (candidateId: string, body: string) =>
    (
      await request<{ id: string }>(`${API_PREFIX}/candidates/${candidateId}/notes`, {
        method: 'POST',
        body: { body },
      })
    ).id,
  deleteNote: (noteId: string) =>
    request<void>(`${API_PREFIX}/notes/${noteId}`, { method: 'DELETE' }),
  createTag: async (name: string, colour: string | null) =>
    (
      await request<{ id: string }>(`${API_PREFIX}/tags`, {
        method: 'POST',
        body: { name, colour },
      })
    ).id,
  updateTag: (tagId: string, name: string, colour: string | null) =>
    request<void>(`${API_PREFIX}/tags/${tagId}`, { method: 'PATCH', body: { name, colour } }),
  deleteTag: (tagId: string) => request<void>(`${API_PREFIX}/tags/${tagId}`, { method: 'DELETE' }),
  tagCandidate: (candidateId: string, tagId: string) =>
    request<void>(`${API_PREFIX}/candidates/${candidateId}/tags/${tagId}`, { method: 'PUT' }),
  untagCandidate: (candidateId: string, tagId: string) =>
    request<void>(`${API_PREFIX}/candidates/${candidateId}/tags/${tagId}`, { method: 'DELETE' }),
  /**
   * T52: a placed candidate becomes a client's contact — one explicit act from their page. The
   * service writes the contact now and files the entry request that takes the number off the
   * record (services/bff/src/routes/contacts.ts); the brain lands on the contact's page.
   */
  moveToContacts: (
    candidateId: string,
    input: { clientId: string; fullName: string | null; talkAsContact: boolean },
  ) =>
    request<{ contactId: string; entryId: string | null }>(
      `${API_PREFIX}/candidates/${candidateId}/move-to-contacts`,
      { method: 'POST', body: input },
    ),
  /**
   * T174: delete the person. The service files the request and hides them at once; the erasure
   * runs within minutes, on a role the brain does not hold (services/bff/src/routes/candidates.ts).
   */
  deletePerson: (candidateId: string) =>
    request<void>(`${API_PREFIX}/candidates/${candidateId}`, { method: 'DELETE' }),
});

export function useCandidates(
  options: ListCandidatesQuery = {},
): UseQueryResult<CandidateListItem[]> {
  const request = useRequest();
  return useQuery({
    queryKey: ['candidates', options],
    queryFn: () => candidatesApi(request).list(options),
    refetchInterval: POLL_MS.slow,
  });
}

/**
 * T273: Candidates → Proactive. Polled as the list is: the stages move as the records change.
 * T292: `keepPrevious` keeps the last answer on screen while a changed filter loads, for the
 * Conversations view, so its list and open threads do not blank between two filters; its rows are
 * then the last filter's (`isPlaceholderData`), and the view opens none of them. The board keeps
 * its loading state.
 */
export function useProactive(
  options: ProactiveQuery = {},
  { keepPrevious = false }: { keepPrevious?: boolean } = {},
): UseQueryResult<ProactiveList> {
  const request = useRequest();
  return useQuery({
    queryKey: ['proactive', options],
    queryFn: () => candidatesApi(request).proactive(options),
    refetchInterval: POLL_MS.slow,
    ...(keepPrevious ? { placeholderData: keepPreviousData } : {}),
  });
}

/** T292a: Proactive's companies, polled as the people are; the last answer kept while a filter loads. */
export function useProactiveCompanies(
  options: ProactiveCompaniesQuery = {},
): UseQueryResult<ProactiveCompany[]> {
  const request = useRequest();
  return useQuery({
    queryKey: ['proactive-companies', options],
    queryFn: () => candidatesApi(request).proactiveCompanies(options),
    refetchInterval: POLL_MS.slow,
    placeholderData: keepPreviousData,
  });
}

/** T296: Searches → Conversations, polled and kept while a filter loads, as Proactive's is. */
export function useSearchConversations(
  options: SearchConversationsQuery = {},
): UseQueryResult<SearchConversationList> {
  const request = useRequest();
  return useQuery({
    queryKey: ['search-conversations', options],
    queryFn: () => candidatesApi(request).searchConversations(options),
    refetchInterval: POLL_MS.slow,
    placeholderData: keepPreviousData,
  });
}

/** T296: Searches' companies, as `useProactiveCompanies`. */
export function useSearchCompanies(options: { q?: string } = {}): UseQueryResult<SearchCompany[]> {
  const request = useRequest();
  return useQuery({
    queryKey: ['search-companies', options],
    queryFn: () => candidatesApi(request).searchCompanies(options),
    refetchInterval: POLL_MS.slow,
    placeholderData: keepPreviousData,
  });
}

/** What a Proactive row opens to read its fit by: a role, or the Search offered straight. */
export type RoleFitRef = { roleId: string } | { searchId: string };
export type RoleFitRead = CandidateDetail['pipeline'][number]['fit'];

/**
 * T456a: the open Proactive row's fit with its requirement rows. Read when a row opens and kept
 * while it is, never on the list's poll: the list carries the score alone.
 */
export function useRoleFit(candidateId: string, ref: RoleFitRef): UseQueryResult<RoleFitRead> {
  const request = useRequest();
  return useQuery({
    queryKey: ['role-fit', candidateId, ref],
    queryFn: () => candidatesApi(request).roleFit(candidateId, ref),
    staleTime: STALE_MS.roleFit,
  });
}

export function useCandidate(candidateId: string): UseQueryResult<CandidateDetail> {
  const request = useRequest();
  return useQuery({
    queryKey: ['candidate', candidateId],
    queryFn: () => candidatesApi(request).get(candidateId),
    refetchInterval: POLL_MS.candidates,
  });
}

export function useTags(): UseQueryResult<TagRow[]> {
  const request = useRequest();
  return useQuery({
    queryKey: ['tags'],
    queryFn: () => candidatesApi(request).listTags(),
    staleTime: STALE_MS.tags,
  });
}

/** T167h: the page's Yes and No on a role kadmon put to the team about this person. */
export const useDecideRoleProposal = () =>
  useConsoleMutation(
    (request, args: { roleProposalId: string; decision: 'yes' | 'no' }) =>
      candidatesApi(request).decideRoleProposal(args.roleProposalId, args.decision),
    KEYS.candidate,
  );

/** T431: the Candidate page's Match Now button. */
export const useMatchNow = () =>
  useConsoleMutation(
    (request, conversationId: string) => candidatesApi(request).matchNow(conversationId),
    KEYS.candidate,
  );

/** T272: the page's Yes and No on their pick of a role offered straight. */
export const useDecidePick = () =>
  useConsoleMutation(
    (request, args: { roleOfferId: string; answer: 'yes' | 'no' }) =>
      candidatesApi(request).decidePick(args.roleOfferId, args.answer),
    KEYS.candidate,
  );

export const usePutForwardPick = () =>
  useConsoleMutation(
    (request, roleOfferId: string) => candidatesApi(request).putForwardPick(roleOfferId),
    KEYS.candidate,
  );

export const useMeetPick = () =>
  useConsoleMutation(
    (request, roleOfferId: string) => candidatesApi(request).meetPick(roleOfferId),
    KEYS.candidate,
  );

export const useAddNote = () =>
  useConsoleMutation(
    (request, args: { candidateId: string; body: string }) =>
      candidatesApi(request).addNote(args.candidateId, args.body),
    KEYS.candidate,
  );

export const useDeleteNote = () =>
  useConsoleMutation(
    (request, noteId: string) => candidatesApi(request).deleteNote(noteId),
    KEYS.candidate,
  );

export const useCreateTag = () =>
  useConsoleMutation(
    (request, args: { name: string; colour: string | null }) =>
      candidatesApi(request).createTag(args.name, args.colour),
    KEYS.candidate,
  );

export const useUpdateTag = () =>
  useConsoleMutation(
    (request, args: { tagId: string; name: string; colour: string | null }) =>
      candidatesApi(request).updateTag(args.tagId, args.name, args.colour),
    KEYS.candidate,
  );

export const useDeleteTag = () =>
  useConsoleMutation(
    (request, tagId: string) => candidatesApi(request).deleteTag(tagId),
    KEYS.candidate,
  );

export const useTagCandidate = () =>
  useConsoleMutation(
    (request, args: { candidateId: string; tagId: string; attached: boolean }) =>
      args.attached
        ? candidatesApi(request).tagCandidate(args.candidateId, args.tagId)
        : candidatesApi(request).untagCandidate(args.candidateId, args.tagId),
    KEYS.candidate,
  );

/**
 * T174: what a delete refetches: every list the person was on (the people, their tags, the
 * Searches, the threads) and never their own page, whose refetch 404s, the person hidden from the
 * moment of the request, and flashed its error before the button navigated away (T202f D-6).
 */
export const DELETE_PERSON_REFETCHES: readonly (readonly unknown[])[] = [
  ['candidates'],
  ['tags'],
  ['searches'],
  ['search'],
  ['roster'],
  ['conversations'],
];

/** T174: a person, deleted. Refetches every list they were on: the people, the Searches, the threads. */
export const useDeletePerson = () =>
  useConsoleMutation(
    (request, candidateId: string) => candidatesApi(request).deletePerson(candidateId),
    DELETE_PERSON_REFETCHES,
  );

/** T52, T430: add them as a client's contact (the number moves only when asked). Refetches the record, the lists and the CRM. */
export const useMoveToContacts = () =>
  useConsoleMutation(
    (
      request,
      args: {
        candidateId: string;
        clientId: string;
        fullName: string | null;
        talkAsContact: boolean;
      },
    ) =>
      candidatesApi(request).moveToContacts(args.candidateId, {
        clientId: args.clientId,
        fullName: args.fullName,
        talkAsContact: args.talkAsContact,
      }),
    [...KEYS.candidate, ...KEYS.client],
  );
